import { useCallback } from 'react';
import {
  setSelectedRequest,
  setSelectedCollection,
  setSelectedFolder,
  setSelectedSchemaEntry,
  useCollectionsStore,
  setHistory,
  setSidebarFadeInIds,
} from '../../store/collectionsStore';
import { setRequestTabSyncRevision } from '../../store/tabsStore';
import { setShowClearHistoryModal } from '../../store/uiModalStore';
import { generateLocalEntityId } from '../../utils/localIds';
import { isTauri } from '../../utils/tauri';
import { readHistoryResponse, writeHistoryResponse, removeHistoryResponses } from '../../services/historyResponseStore';
import { openFolderTab } from '../../utils/folderTabs';
import { isApiHistoryEnabled } from '../../extensions/store';

/** Cap per-entry response body kept in history. */
const MAX_HISTORY_BODY_BYTES = 200 * 1024;

/** Total response-body budget retained across all history entries; protects the ~5MB localStorage quota. */
const HISTORY_RESPONSE_BUDGET_BYTES = 1024 * 1024;

/**
 * Keep full responses only on the most-recent entries up to a total byte budget; evict the heavy
 * fields (body + headers) on older entries so the persisted history stays well under quota. The
 * lightweight metadata (status, responseTime, request fields) is preserved so rows still render.
 */
function pruneHistoryResponses(history) {
  let used = 0;
  return history.map((item) => {
    if (!item.response || typeof item.response.data !== 'string') return item;
    const size = item.response.data.length;
    if (used + size > HISTORY_RESPONSE_BUDGET_BYTES) {
      return { ...item, response: { ...item.response, data: '', headers: {}, evicted: true } };
    }
    used += size;
    return item;
  });
}

/**
 * Deletes every history entry, and the response files of the ones kept on
 * disk. Shared by the History page's Clear (after its confirm) and the
 * API Client card in Settings → Extensions.
 */
export function clearAllRequestHistory() {
  const current = useCollectionsStore.getState().history || [];
  const ids = current.filter((h) => h.response?.onDisk && h.id).map((h) => h.id);
  if (ids.length) removeHistoryResponses(ids).catch(() => {});
  setHistory([]);
}

/** Folder selection, request history (add, select, clear) and the sidebar flash. */
export function useRequestHistory({
  toastT,
  createNewRequestTab,
  updateCurrentRequestTabStable,
  getCurrentRequestTab,
}) {
  // ---- Selection ------------------------------------------------------------
  // A folder row click opens the folder PAGE, like a collection row (owner D1).
  const selectFolder = useCallback((folder, collectionId) => {
    setSelectedFolder({ ...folder, collectionId });
    setSelectedRequest(null);
    setSelectedCollection(null);
    setSelectedSchemaEntry(null);
    openFolderTab(collectionId, folder?.id);
  }, []);

  // ---- History --------------------------------------------------------------
  const selectFromHistory = useCallback(async (historyItem, options = {}) => {
    // Full response lives on the filesystem (Tauri); read it back. Falls back to the
    // inline stub/body for web entries or if the file is missing.
    let savedResponse = historyItem.response || null;
    if (savedResponse?.onDisk && historyItem.id) {
      const fromDisk = await readHistoryResponse(historyItem.id);
      if (fromDisk) savedResponse = fromDisk;
    }
    const requestData = {
      method: historyItem.method,
      url: historyItem.url,
      headers: historyItem.headers || [{ key: '', value: '', enabled: true }],
      params: historyItem.params || [{ key: '', value: '', enabled: true }],
      cookies: historyItem.cookies || [{ key: '', value: '', enabled: true }],
      body: historyItem.body || '',
      bodyType: historyItem.bodyType || 'json',
      auth: historyItem.auth,
      formDataFields: historyItem.formDataFields,
      response: savedResponse, // restores the saved response into the response Monaco editor
    };

    // Preview path: hand the resolved request+response back to the caller so it can apply
    // it non-destructively (no "unsaved" flag) instead of mutating the tab here.
    if (options.resolveOnly) return requestData;

    const currentTab = getCurrentRequestTab();
    if (options.intoCurrentTab && currentTab) {
      // In-builder history popover: always populate the current tab in place
      updateCurrentRequestTabStable(requestData);
      setRequestTabSyncRevision((n) => n + 1); // force editor to re-sync all fields
    } else if (currentTab && !currentTab.url && !currentTab.hasUnsavedChanges) {
      // Update empty current tab
      updateCurrentRequestTabStable(requestData);
    } else {
      // Create new tab
      createNewRequestTab(requestData);
    }
  }, [getCurrentRequestTab, updateCurrentRequestTabStable, createNewRequestTab]);

  const addToHistory = useCallback((requestData, response) => {
    // History is part of the API Client: while it is switched off nothing is
    // recorded — not the entry, not the response file (apiClientSwitch.js).
    if (!isApiHistoryEnabled()) return;
    const id = generateLocalEntityId('hist');
    // Desktop (Tauri): persist the full response to the filesystem and keep only a
    // lightweight stub in localStorage. Web/dev: fall back to a budgeted inline body.
    const onDisk = isTauri() && !!response;
    let savedResponse = null;
    if (response) {
      savedResponse = {
        status: response.status,
        statusText: response.statusText,
        time: response.time,
        size: response.size,
        url: response.url,
        onDisk,
      };
      if (onDisk) {
        // Fire-and-forget: the row only needs the stub; the body is read on click.
        writeHistoryResponse(id, response).catch(() => {});
      } else {
        const data = typeof response.data === 'string' ? response.data : '';
        const truncated = data.length > MAX_HISTORY_BODY_BYTES;
        savedResponse.headers = response.headers || {};
        savedResponse.data = truncated ? data.slice(0, MAX_HISTORY_BODY_BYTES) : data;
        savedResponse.truncated = truncated;
      }
    }
    const historyItem = {
      ...requestData,
      id,
      timestamp: Date.now(),
      status: response?.status,
      responseTime: response?.time,
      response: savedResponse,
    };

    const prev = useCollectionsStore.getState().history;
    const next = [historyItem, ...prev.slice(0, 99)]; // keep last 100
    // Delete on-disk response files for entries pushed out of the window.
    const droppedIds = prev.slice(99).filter((h) => h.response?.onDisk && h.id).map((h) => h.id);
    if (droppedIds.length) removeHistoryResponses(droppedIds).catch(() => {});
    // Web fallback keeps a total inline-body budget; Tauri stubs are already tiny.
    setHistory(onDisk ? next : pruneHistoryResponses(next));
  }, []);

  const clearHistory = useCallback(() => {
    setShowClearHistoryModal(true);
  }, []);

  const handleClearHistory = useCallback(() => {
    clearAllRequestHistory();
    setShowClearHistoryModal(false);
    toastT('toast.historyCleared', 'success');
  }, [toastT]);

  // Clear only the entries belonging to a single request (used by the in-builder popover).
  const clearHistoryForRequest = useCallback((requestKey) => {
    if (requestKey == null) return;
    const key = String(requestKey);
    const current = useCollectionsStore.getState().history;
    const ids = current
      .filter((h) => String(h.requestKey) === key && h.response?.onDisk && h.id)
      .map((h) => h.id);
    if (ids.length) removeHistoryResponses(ids).catch(() => {});
    setHistory(current.filter((h) => String(h.requestKey) !== key));
    toastT('toast.historyCleared', 'success');
  }, [toastT]);

  // ---- Sidebar flash (newly created/duplicated entry) ----------------------
  const flashSidebarEntry = useCallback((id) => {
    if (id == null) return;
    const key = String(id);
    setSidebarFadeInIds((prev) => new Set([...prev, key]));
    window.setTimeout(() => {
      setSidebarFadeInIds((prev) => {
        if (!prev.has(key)) return prev;
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }, 300);
  }, []);

  return {
    selectFolder,
    selectFromHistory,
    addToHistory,
    clearHistory,
    handleClearHistory,
    clearHistoryForRequest,
    flashSidebarEntry,
  };
}
